import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "../data/content";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const slug = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-");

const ServiceCard = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <motion.article
      data-testid={`service-card-${slug(s.title)}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`glow-card group ${s.span || ""}`}
    >
      <div className={`relative h-full rounded-[5px] bg-[#1C1E24] overflow-hidden ${s.img ? "grid md:grid-cols-2" : ""}`}>
        <div className="relative p-7 sm:p-8 flex flex-col h-full min-h-[300px]">
          <div className="flex items-start justify-between">
            <span className="grid place-items-center h-12 w-12 rounded-[4px] bg-[#0B0C10] border border-white/10 text-[#FF6B00] transition-colors duration-300 group-hover:bg-[#FF6B00] group-hover:text-[#0B0C10]">
              <Icon className="h-5 w-5" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400 border border-white/10 rounded-full px-2.5 py-1">{s.tag}</span>
          </div>
          <p className="mt-8 font-mono text-xs text-zinc-400">0{i + 1}</p>
          <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-white">{s.title}</h3>
          {s.desc && <p className="mt-4 text-[15px] text-zinc-300 leading-relaxed">{s.desc}</p>}
          <ul className="mt-5 flex flex-wrap gap-2">
            {s.items.map((it) => (
              <li key={it} className="text-[13px] text-zinc-300 bg-white/[0.04] border border-white/10 rounded-full px-3 py-1">{it}</li>
            ))}
          </ul>
          <button onClick={() => scrollToId("contact")} data-testid={`service-cta-${slug(s.title)}`} className="mt-auto pt-8 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:text-[#FF6B00] transition-colors self-start">
            Request this service <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>
        {s.img && (
          <div className="relative min-h-[220px] overflow-hidden">
            <img src={s.img} alt={s.title} className="absolute inset-0 h-full w-full object-cover grayscale-[40%] transition-transform duration-[1.2s] ease-out group-hover:scale-105 group-hover:grayscale-0" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1C1E24] via-transparent to-transparent hidden md:block" />
          </div>
        )}
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative bg-[#0B0C10] py-24 sm:py-32 overflow-hidden">
    <div className="absolute inset-0 tactical-grid opacity-50" />
    <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-8 mb-14">
        <Reveal className="lg:col-span-7">
          <Eyebrow>Services</Eyebrow>
          <h2 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.95] text-white">
            One vendor. <span className="text-zinc-400">Fewer moving parts.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9 self-end">
          <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
            From recurring operations and repairs to custom builds and real estate guidance — handled in-house, or coordinated under a single point of accountability.
          </p>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.title} s={s} i={i} />
        ))}
      </div>
    </div>
  </section>
);
